import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  X,
  ZoomIn,
  ZoomOut,
  RotateCw,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Attachment {
  id?: string;
  filename: string;
  url: string;
  mime_type?: string;
}

interface AttachmentPreviewProps {
  attachments: Attachment[];
  initialIndex: number;
  onClose: () => void;
}

export const AttachmentPreview = ({
  attachments,
  initialIndex,
  onClose,
}: AttachmentPreviewProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  const currentAttachment = attachments[currentIndex];

  const isImage = (attachment: Attachment) => {
    return (
      attachment.mime_type?.startsWith("image/") ||
      /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(attachment.filename)
    );
  };

  const isPDF = (attachment: Attachment) => {
    return (
      attachment.mime_type === "application/pdf" ||
      attachment.filename.toLowerCase().endsWith(".pdf")
    );
  };

  const isVideo = (attachment: Attachment) => {
    return (
      attachment.mime_type?.startsWith("video/") ||
      /\.(mp4|mov|avi|webm)$/i.test(attachment.filename)
    );
  };

  const isAudio = (attachment: Attachment) => {
    return (
      attachment.mime_type?.startsWith("audio/") ||
      /\.(mp3|wav|ogg|m4a)$/i.test(attachment.filename)
    );
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : attachments.length - 1));
    resetTransforms();
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < attachments.length - 1 ? prev + 1 : 0));
    resetTransforms();
  };

  const resetTransforms = () => {
    setZoom(1);
    setRotation(0);
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleDownload = () => {
    window.open(currentAttachment.url, "_blank");
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowLeft":
          goToPrevious();
          break;
        case "ArrowRight":
          goToNext();
          break;
        case "Escape":
          onClose();
          break;
        case "+":
        case "=":
          handleZoomIn();
          break;
        case "-":
          handleZoomOut();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const renderPreview = () => {
    if (!currentAttachment) return null;

    if (isImage(currentAttachment)) {
      return (
        <div className="flex items-center justify-center h-full overflow-hidden">
          <img
            src={currentAttachment.url}
            alt={currentAttachment.filename}
            className="max-w-full max-h-full object-contain transition-transform duration-200"
            style={{
              transform: `scale(${zoom}) rotate(${rotation}deg)`,
            }}
          />
        </div>
      );
    }

    if (isPDF(currentAttachment)) {
      return (
        <iframe
          src={`${currentAttachment.url}#toolbar=0`}
          className="w-full h-full rounded-lg"
          title={currentAttachment.filename}
        />
      );
    }

    if (isVideo(currentAttachment)) {
      return (
        <video
          src={currentAttachment.url}
          controls
          className="max-w-full max-h-full"
        >
          Your browser does not support the video tag.
        </video>
      );
    }

    if (isAudio(currentAttachment)) {
      return (
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="h-32 w-32 rounded-full bg-muted flex items-center justify-center">
            <span className="text-4xl">🎵</span>
          </div>
          <p className="text-lg font-medium">{currentAttachment.filename}</p>
          <audio src={currentAttachment.url} controls className="w-full max-w-md">
            Your browser does not support the audio tag.
          </audio>
        </div>
      );
    }

    // Fallback for other file types
    return (
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="h-24 w-24 rounded-lg bg-muted flex items-center justify-center">
          <span className="text-4xl">📄</span>
        </div>
        <p className="text-lg font-medium">{currentAttachment.filename}</p>
        <p className="text-sm text-muted-foreground">
          Preview not available for this file type
        </p>
        <Button onClick={handleDownload} className="gap-2">
          <Download className="h-4 w-4" />
          Download
        </Button>
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-0 gap-0">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-background/95 backdrop-blur">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate max-w-[300px]">
              {currentAttachment?.filename}
            </span>
            <span className="text-sm text-muted-foreground">
              {currentIndex + 1} of {attachments.length}
            </span>
          </div>

          <div className="flex items-center gap-1">
            {isImage(currentAttachment) && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleZoomOut}
                  disabled={zoom <= 0.5}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm w-12 text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleZoomIn}
                  disabled={zoom >= 3}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={handleRotate}>
                  <RotateCw className="h-4 w-4" />
                </Button>
                <div className="w-px h-6 bg-border mx-1" />
              </>
            )}
            <Button variant="ghost" size="sm" onClick={handleDownload}>
              <Download className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Preview area */}
        <div className="flex-1 relative overflow-hidden bg-black/5 dark:bg-white/5">
          {renderPreview()}

          {/* Navigation arrows */}
          {attachments.length > 1 && (
            <>
              <Button
                variant="secondary"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full shadow-lg"
                onClick={goToPrevious}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full shadow-lg"
                onClick={goToNext}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </>
          )}
        </div>

        {/* Thumbnail strip */}
        {attachments.length > 1 && (
          <div className="flex gap-2 p-4 border-t overflow-x-auto bg-background">
            {attachments.map((attachment, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentIndex(index);
                  resetTransforms();
                }}
                className={cn(
                  "h-16 w-16 rounded-lg overflow-hidden shrink-0 transition-all",
                  index === currentIndex
                    ? "ring-2 ring-primary"
                    : "opacity-60 hover:opacity-100"
                )}
              >
                {isImage(attachment) ? (
                  <img
                    src={attachment.url}
                    alt={attachment.filename}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center text-2xl">
                    📄
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
